/**
 * Visual event content-block editor (Phase 4.5-B).
 *
 * Layout:
 *   Left column  = block palette + ordered list of blocks (with hide/show, reorder, delete, edit trigger)
 *   Right column = focused block editor OR a lightweight live preview when nothing is selected
 *
 * Per-block editors are friendly forms with plain-English labels. No raw JSON is exposed.
 * Rich-text blocks use a small contenteditable toolbar (bold / italic / list / link).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { resizeImageIfNeeded } from '@/utils/imageResize';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  ChevronUp, ChevronDown, Plus, Trash2, Eye, EyeOff, Save,
  Type, ImageIcon as ImageBlockIcon, MapPin, CalendarDays, ListChecks,
  UtensilsCrossed, Shirt, HelpCircle, ScrollText, Users, Award,
  FileText, Ticket, PlayCircle, Bold, Italic, List as ListIcon, Link2, Upload, X,
  Sparkles, UploadCloud, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import DostDraftDialog from '@/components/dost/DostDraftDialog';
import DostPolishTextButton from '@/components/dost/DostPolishTextButton';
import DostCoPilotPanel from './DostCoPilotPanel';
import HostPhotoCropperDialog from './HostPhotoCropperDialog';
import { CopyFromPreviousEventDialog } from './CopyFromPreviousEventDialog';

const BLOCK_META = {
  hero:              { label: 'Hero',              icon: ImageBlockIcon, cat: 'header'  },
  overview:          { label: 'Overview',          icon: Type,           cat: 'text'    },
  date_time:         { label: 'Date & Time',       icon: CalendarDays,   cat: 'struct'  },
  venue:             { label: 'Venue',             icon: MapPin,         cat: 'struct'  },
  schedule:          { label: 'Schedule',          icon: ListChecks,     cat: 'struct'  },
  what_to_expect:    { label: 'What to Expect',    icon: Type,           cat: 'text'    },
  food:              { label: 'Food',              icon: UtensilsCrossed, cat: 'struct' },
  gallery:           { label: 'Gallery',           icon: ImageBlockIcon, cat: 'media'   },
  dress_code:        { label: 'Dress Code',        icon: Shirt,          cat: 'struct'  },
  faq:               { label: 'FAQ',               icon: HelpCircle,     cat: 'struct'  },
  policies:          { label: 'Policies',          icon: ScrollText,     cat: 'struct'  },
  hosts:             { label: 'Hosts',             icon: Users,          cat: 'struct'  },
  sponsors:          { label: 'Sponsors',          icon: Award,          cat: 'struct'  },
  custom_rich_text:  { label: 'Custom text',       icon: FileText,       cat: 'text'    },
  rsvp_cta:          { label: 'RSVP button',       icon: Ticket,         cat: 'header'  },
  video:             { label: 'Video',             icon: PlayCircle,     cat: 'media'   },
};

const ALL_BLOCK_TYPES = Object.keys(BLOCK_META);

const VisualBlockEditor = ({ eventId, event, blocks, onChanged }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [scaffoldOpen, setScaffoldOpen] = useState(false);
  const [scaffoldBusy, setScaffoldBusy] = useState(false);
  const [scaffoldResult, setScaffoldResult] = useState(null);
  const [scaffoldErr, setScaffoldErr] = useState('');
  // Bundle 182 - copy content from previous event.
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [recentlyImportedTypes, setRecentlyImportedTypes] = useState([]);
  // Co-pilot "Fix with Dost" -> pre-loaded polish compare dialog
  const [fixState, setFixState] = useState(null);
  // { open, critique, blockType, currentText, proposedText, meta, targetBlockId }
  const [fixBusy, setFixBusy] = useState(false);

  const sorted = useMemo(() => (blocks || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)), [blocks]);
  const selected = sorted.find((b) => b.id === selectedId) || null;

  const extractBlockText = (b) => {
    const c = b?.content || {};
    if (c.body_html) return { text: c.body_html, isHtml: true, key: 'body_html' };
    if (c.body) return { text: c.body, isHtml: false, key: 'body' };
    if (c.dress_code) return { text: c.dress_code, isHtml: false, key: 'dress_code' };
    return { text: '', isHtml: false, key: 'body' };
  };

  const runFixWithDost = async (critique) => {
    const target = sorted.find((b) => b.block_type === critique.block_type);
    if (target) setSelectedId(target.id);
    const { text: currentText, isHtml, key } = target ? extractBlockText(target) : { text: '', isHtml: false, key: 'body' };
    setFixState({
      open: true,
      critique,
      blockType: critique.block_type,
      currentText,
      proposedText: '',
      meta: { latency_ms: 0, tokens_used: 0 },
      targetBlockId: target?.id || null,
      isHtml,
      contentKey: key,
    });
    setFixBusy(true);
    try {
      const { data } = await api.post('/dost/admin/apply-critique', {
        event_id: eventId,
        block_type: critique.block_type,
        current_text: currentText,
        critique_message: critique.message,
        tone: 'warm',
      });
      setFixState((cur) => cur ? ({
        ...cur,
        proposedText: data.proposed_text || '',
        meta: { latency_ms: data.latency_ms || 0, tokens_used: data.tokens_used || 0 },
      }) : cur);
    } catch (e) {
      setFixState((cur) => cur ? ({ ...cur, proposedText: '', errored: true }) : cur);
    } finally {
      setFixBusy(false);
    }
  };

  const acceptFix = async (newText) => {
    if (!fixState) return;
    if (fixState.targetBlockId) {
      const target = sorted.find((b) => b.id === fixState.targetBlockId);
      if (target) {
        const nextContent = { ...(target.content || {}), [fixState.contentKey]: newText };
        await api.patch(`/admin/events/${eventId}/blocks/${target.id}`, { content: nextContent });
        await onChanged();
      }
    }
    setFixState(null);
  };

  const scaffold = async (overwrite) => {
    setScaffoldBusy(true); setScaffoldErr(''); setScaffoldResult(null);
    try {
      const { data } = await api.post('/dost/admin/scaffold-event', {
        event_id: eventId,
        tone: 'warm',
        overwrite_existing: !!overwrite,
      });
      setScaffoldResult(data);
      await onChanged();
    } catch (e) {
      const s = e?.response?.status;
      const detail = e?.response?.data?.detail || '';
      if (s === 429) setScaffoldErr("You've used today's scaffolds. Try again tomorrow.");
      else setScaffoldErr(detail || 'Dost had trouble scaffolding. Try again shortly.');
    } finally {
      setScaffoldBusy(false);
    }
  };

  const addBlock = async (blockType) => {
    setPickerOpen(false);
    const { data } = await api.post(`/admin/events/${eventId}/blocks`, {
      block_type: blockType,
      hidden: false,
      content: {},
    });
    await onChanged();
    setSelectedId(data.block_id);
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= sorted.length) return;
    const arr = [...sorted];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    await api.post(`/admin/events/${eventId}/blocks/reorder`, arr.map((b) => b.id));
    await onChanged();
  };

  const toggleHidden = async (b) => {
    await api.patch(`/admin/events/${eventId}/blocks/${b.id}`, { hidden: !b.hidden });
    await onChanged();
  };

  const del = async (b) => {
    if (!window.confirm('Delete this block?')) return;
    if (selectedId === b.id) setSelectedId(null);
    await api.delete(`/admin/events/${eventId}/blocks/${b.id}`);
    await onChanged();
  };

  const saveContent = async (b, content) => {
    await api.patch(`/admin/events/${eventId}/blocks/${b.id}`, { content });
    await onChanged();
  };

  return (
    <div className="grid gap-6 md:grid-cols-[minmax(280px,340px)_1fr] xl:grid-cols-[minmax(280px,340px)_1fr_320px]" data-testid="visual-block-editor">
      {/* Left column: palette + list */}
      <div className="space-y-3">
        <Card className="border shadow-soft">
          <CardContent className="p-4 space-y-2">
            <Button
              onClick={() => setPickerOpen(true)}
              className="w-full bg-primary-800 hover:bg-primary-900 text-white"
              data-testid="add-block-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add block
            </Button>
            <Button
              variant="outline"
              onClick={() => { setScaffoldResult(null); setScaffoldErr(''); setScaffoldOpen(true); }}
              className="w-full border-secondary-400 text-secondary-800 hover:bg-secondary-50"
              data-testid="scaffold-with-dost-btn"
              title="Draft empty content blocks from your Basics info"
            >
              <Sparkles className="h-4 w-4 mr-1.5" /> Scaffold event with Dost
            </Button>
            <Button
              variant="outline"
              onClick={() => setCopyDialogOpen(true)}
              className="w-full border-primary-300 text-primary-800 hover:bg-primary-50"
              data-testid="copy-from-previous-btn"
              title="Copy hosts, sponsors, FAQ, policies, and more from a previous event"
            >
              <Copy className="h-4 w-4 mr-1.5" /> Copy from previous event
            </Button>
          </CardContent>
        </Card>

        <div className="space-y-1.5">
          {sorted.length === 0 && (
            <div className="p-6 text-center text-sm text-neutral-500 border border-dashed border-neutral-200 rounded-xl">
              No blocks yet. Click &quot;Add block&quot; to start.
            </div>
          )}
          {sorted.map((b, i) => (
            <BlockRow
              key={b.id}
              block={b}
              index={i}
              total={sorted.length}
              selected={selectedId === b.id}
              onSelect={() => setSelectedId(b.id)}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, +1)}
              onToggleHidden={() => toggleHidden(b)}
              onDelete={() => del(b)}
              isRecentlyImported={
                recentlyImportedTypes.includes(b.block_type)
              }
            />
          ))}
        </div>
      </div>

      {/* Right column: editor or preview */}
      <div>
        {selected ? (
          <BlockEditorFocus
            key={selected.id}
            eventId={eventId}
            event={event}
            block={selected}
            onSave={(content) => saveContent(selected, content)}
            onClose={() => setSelectedId(null)}
          />
        ) : (
          <LivePreview blocks={sorted} onSelect={setSelectedId} />
        )}
      </div>

      {/* Co-pilot column (xl and up; auto-collapses to floating pill on smaller screens) */}
      <div>
        <DostCoPilotPanel
          eventId={eventId}
          blocks={sorted}
          onApplyCritique={(critique) => runFixWithDost(critique)}
        />
      </div>

      {/* Block picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Add a block</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
            {ALL_BLOCK_TYPES.map((t) => {
              const meta = BLOCK_META[t];
              const Icon = meta.icon;
              return (
                <button
                  key={t}
                  onClick={() => addBlock(t)}
                  className="group text-left p-3 rounded-xl border border-neutral-200 hover:border-secondary-400 hover:bg-secondary-50/30 transition-colors"
                  data-testid={`add-block-type-${t}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary-50 text-primary-800 flex items-center justify-center group-hover:bg-secondary-100 group-hover:text-secondary-800 transition-colors">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="text-sm font-semibold text-primary-900">{meta.label}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dost scaffold dialog */}
      <Dialog open={scaffoldOpen} onOpenChange={setScaffoldOpen}>
        <DialogContent className="max-w-lg" data-testid="scaffold-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-secondary-700" /> Scaffold event with Dost
            </DialogTitle>
          </DialogHeader>
          {!scaffoldResult && !scaffoldErr && (
            <div className="space-y-4 text-sm text-neutral-700">
              <p>
                Dost will draft your <strong>Overview</strong>, <strong>What to Expect</strong>,
                {' '}<strong>Food</strong>, <strong>Dress Code</strong>, and <strong>FAQ</strong> blocks
                based on your Basics info. Existing content in those blocks won&apos;t be touched
                unless you enable Overwrite.
              </p>
              <div className="p-3 rounded-md bg-neutral-50 border border-neutral-200 text-xs text-neutral-600">
                Dost never invents venue features, prices, or host names. If a fact isn&apos;t in your
                Basics, it either skips that detail or notes it isn&apos;t finalized. Review every
                block before publishing.
              </div>
              {scaffoldBusy && (
                <div className="flex items-center gap-2 text-secondary-800">
                  <Sparkles className="h-4 w-4 animate-pulse" />
                  <span>Dost is drafting your event, up to 15 seconds…</span>
                </div>
              )}
            </div>
          )}
          {scaffoldResult && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-success-700 font-semibold">
                <Sparkles className="h-4 w-4" />
                Done. {scaffoldResult.scaffolded_blocks.length} block{scaffoldResult.scaffolded_blocks.length === 1 ? '' : 's'} drafted.
              </div>
              {scaffoldResult.scaffolded_blocks.length > 0 && (
                <ul className="space-y-2">
                  {scaffoldResult.scaffolded_blocks.map((b) => (
                    <li key={b.block_type} className="p-2 rounded-md border border-secondary-200 bg-secondary-50/30" data-testid={`scaffold-result-${b.block_type}`}>
                      <div className="text-[11px] uppercase tracking-widest text-secondary-800 font-semibold">
                        {BLOCK_META[b.block_type]?.label || b.block_type}
                      </div>
                      <div className="text-xs text-neutral-700 line-clamp-2">{b.content_preview}</div>
                    </li>
                  ))}
                </ul>
              )}
              {scaffoldResult.skipped_blocks && scaffoldResult.skipped_blocks.length > 0 && (
                <div className="text-xs text-neutral-500">
                  Skipped: {scaffoldResult.skipped_blocks.map((s) => (BLOCK_META[s.block_type]?.label || s.block_type)).join(', ')} (already had content).
                </div>
              )}
              <div className="text-[11px] text-neutral-500">
                {scaffoldResult.latency_ms}ms · {scaffoldResult.tokens_used} tokens
              </div>
            </div>
          )}
          {scaffoldErr && (
            <div className="p-3 rounded-md bg-error-50 text-error-800 text-sm" data-testid="scaffold-error">
              {scaffoldErr}
            </div>
          )}
          <DialogFooter className="gap-2">
            {!scaffoldResult && !scaffoldErr && (
              <>
                <Button variant="ghost" onClick={() => setScaffoldOpen(false)} disabled={scaffoldBusy}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={() => scaffold(true)}
                  disabled={scaffoldBusy}
                  data-testid="scaffold-overwrite"
                >
                  Regenerate all (overwrite)
                </Button>
                <Button
                  onClick={() => scaffold(false)}
                  className="bg-primary-800 hover:bg-primary-900 text-white"
                  disabled={scaffoldBusy}
                  data-testid="scaffold-empty-only"
                >
                  <Sparkles className="h-4 w-4 mr-1.5" /> Draft empty blocks
                </Button>
              </>
            )}
            {(scaffoldResult || scaffoldErr) && (
              <Button onClick={() => setScaffoldOpen(false)} className="bg-primary-800 hover:bg-primary-900 text-white">
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Co-pilot "Fix with Dost" pre-loaded compare dialog */}
      {fixState && (
        <DostPolishTextButton
          externalOpen={!!fixState.open}
          onExternalClose={() => setFixState(null)}
          initialProposed={fixBusy ? '' : (fixState.proposedText || '')}
          initialMeta={fixState.meta}
          blockType={fixState.blockType}
          isHtml={!!fixState.isHtml}
          value={fixState.currentText}
          onChange={acceptFix}
          titleOverride={fixBusy
            ? 'Dost is preparing a fix…'
            : `Address critique: ${(fixState.critique?.message || '').slice(0, 80)}${(fixState.critique?.message || '').length > 80 ? '…' : ''}`
          }
          regenerateEndpoint="/dost/admin/apply-critique"
          regeneratePayload={{
            event_id: eventId,
            block_type: fixState.blockType,
            current_text: fixState.currentText,
            critique_message: fixState.critique?.message || '',
            tone: 'warm',
          }}
        />
      )}
      {/* Bundle 182 - Copy content from previous event modal */}
      <CopyFromPreviousEventDialog
        open={copyDialogOpen}
        eventId={eventId}
        onOpenChange={setCopyDialogOpen}
        onImported={async (info) => {
          await onChanged();
          const types = info?.block_types_imported || [];
          if (types.length > 0) {
            setRecentlyImportedTypes(types);
            // Fade the pulse marker after 30s.
            setTimeout(() => setRecentlyImportedTypes([]), 30000);
          }
        }}
      />
    </div>
  );
};

const BlockRow = ({ block, index, total, selected, onSelect, onMoveUp, onMoveDown, onToggleHidden, onDelete, isRecentlyImported }) => {
  const meta = BLOCK_META[block.block_type] || { label: block.block_type, icon: Type };
  const Icon = meta.icon;
  const title = block.content?.title || meta.label;
  return (
    <div
      className={`flex items-center gap-2 p-2.5 rounded-lg border ${selected ? 'border-secondary-400 bg-secondary-50/40 shadow-gold' : isRecentlyImported ? 'border-primary-400 bg-primary-50/50 animate-pulse' : 'border-neutral-200 bg-white'} ${block.hidden ? 'opacity-60' : ''} hover:border-secondary-300 transition-colors`}
      data-testid={`block-row-${block.id}`}
    >
      <div className="flex flex-col text-neutral-400">
        <button onClick={onMoveUp} disabled={index === 0} className="disabled:opacity-30 hover:text-primary-900"><ChevronUp className="h-3.5 w-3.5" /></button>
        <button onClick={onMoveDown} disabled={index === total - 1} className="disabled:opacity-30 hover:text-primary-900"><ChevronDown className="h-3.5 w-3.5" /></button>
      </div>
      <button onClick={onSelect} className="flex-1 flex items-center gap-2 min-w-0 text-left" data-testid={`block-row-select-${block.id}`}>
        <div className="w-7 h-7 rounded-md bg-primary-50 text-primary-800 flex items-center justify-center flex-shrink-0">
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-primary-900 truncate flex items-center gap-1.5">
            {title}
            {block.scaffolded_by_dost && (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-secondary-100 border border-secondary-300 text-[9px] uppercase tracking-widest text-secondary-800 font-semibold"
                title="Drafted by Dost - review before publishing"
                data-testid={`block-scaffolded-chip-${block.block_type}`}
              >
                <Sparkles className="h-2.5 w-2.5" /> Dost
              </span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">{meta.label}</div>
        </div>
      </button>
      <button onClick={onToggleHidden} className="text-neutral-500 hover:text-primary-900 p-1" data-testid={`block-row-toggle-${block.id}`}>
        {block.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
      <button onClick={onDelete} className="text-neutral-400 hover:text-accent-700 p-1" data-testid={`block-row-delete-${block.id}`}>
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

/* --------------------------- FOCUS EDITOR ---------------------------- */

const BlockEditorFocus = ({ eventId, event, block, onSave, onClose }) => {
  const [content, setContent] = useState(block.content || {});
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);
  const [dostOpen, setDostOpen] = useState(false);

  const meta = BLOCK_META[block.block_type] || { label: block.block_type, icon: Type };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(content);
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1200);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const Editor = editorForType(block.block_type);
  const isTextBlock = ['overview', 'what_to_expect', 'custom_rich_text', 'policies'].includes(block.block_type);

  return (
    <Card className="border shadow-soft" data-testid={`block-focus-${block.id}`}>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-neutral-100 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-800 flex items-center justify-center">
              <meta.icon className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display text-lg font-bold text-primary-900">{meta.label}</div>
              {block.hidden && <Badge variant="outline" className="text-[10px] mt-0.5">Hidden on public page</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isTextBlock && (
              <Button
                variant="outline" size="sm"
                onClick={() => setDostOpen(true)}
                className="border-secondary-300 text-secondary-800 hover:bg-secondary-50"
                data-testid="block-draft-with-dost-btn"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Draft with Dost
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="block-focus-close">Close</Button>
          </div>
        </div>

        <Editor content={content} onChange={setContent} event={event} />

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-neutral-100">
          {savedTick && <span className="text-xs text-success-700 mr-2">Saved</span>}
          <Button onClick={save} disabled={saving} className="bg-primary-800 hover:bg-primary-900 text-white" data-testid="block-focus-save">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>

        <DostDraftDialog
          open={dostOpen}
          onClose={() => setDostOpen(false)}
          eventId={eventId}
          blockType={block.block_type}
          onAccept={(text) => setContent((c) => ({ ...c, body_html: text.replace(/\n/g, '<br/>') }))}
        />
      </CardContent>
    </Card>
  );
};

/* -------------------- PER-TYPE FRIENDLY EDITORS ----------------------- */

function editorForType(t) {
  switch (t) {
    case 'overview':
    case 'what_to_expect':
    case 'custom_rich_text':
      return RichTextBlockEditor;
    case 'hero':          return HeroBlockEditor;
    case 'date_time':     return DateTimeBlockEditor;
    case 'venue':         return VenueBlockEditor;
    case 'schedule':      return ScheduleBlockEditor;
    case 'food':          return FoodBlockEditor;
    case 'gallery':       return GalleryBlockEditor;
    case 'dress_code':    return DressCodeBlockEditor;
    case 'faq':           return FaqBlockEditor;
    case 'policies':      return PoliciesBlockEditor;
    case 'hosts':         return HostsBlockEditor;
    case 'sponsors':      return SponsorsBlockEditor;
    case 'rsvp_cta':      return RsvpCtaBlockEditor;
    case 'video':         return VideoBlockEditor;
    default:              return DefaultBlockEditor;
  }
}

const FieldRow = ({ label, hint, action, children }) => (
  <div>
    <div className="flex items-center justify-between gap-2">
      <Label className="text-[11px] uppercase tracking-widest text-secondary-700">{label}</Label>
      {action}
    </div>
    <div className="mt-1">{children}</div>
    {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
  </div>
);

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const ref = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  // A3 fix: contentEditable + dangerouslySetInnerHTML get out of sync when a
  // parent hands us a new `value` (e.g. after "Use this" on Polish with Dost).
  // React's dangerouslySetInnerHTML does not reliably re-write innerHTML
  // once contentEditable has mutated the DOM, so we mirror the prop into the
  // DOM manually whenever it diverges. We only touch the DOM if the browser's
  // current innerHTML differs from the incoming prop, so the user's caret is
  // preserved during normal typing (onInput -> onChange -> parent state ->
  // same string comes back here).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = value || '';
    if (el.innerHTML !== incoming) {
      el.innerHTML = incoming;
    }
  }, [value]);

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(ref.current?.innerHTML || '');
  };

  const applyLink = () => {
    if (linkUrl) exec('createLink', linkUrl);
    setLinkOpen(false); setLinkUrl('');
  };

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-1 p-1.5 border-b border-neutral-100 bg-neutral-50/60">
        <ToolbarBtn onClick={() => exec('bold')} title="Bold"><Bold className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('italic')} title="Italic"><Italic className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('insertUnorderedList')} title="Bullet list"><ListIcon className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => setLinkOpen(true)} title="Add link"><Link2 className="h-3.5 w-3.5" /></ToolbarBtn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className="min-h-[160px] p-3 text-sm text-primary-900 focus:outline-none prose prose-sm max-w-none"
        data-testid="rich-text-editor-area"
        data-placeholder={placeholder || 'Write here…'}
      />
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add link</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" autoFocus />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setLinkOpen(false)}>Cancel</Button>
              <Button onClick={applyLink} className="bg-primary-800 hover:bg-primary-900 text-white">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ToolbarBtn = ({ onClick, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    className="p-1.5 rounded hover:bg-neutral-200 text-primary-800"
  >
    {children}
  </button>
);

const RichTextBlockEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <FieldRow label="Section title">
      <Input value={content.title || ''} onChange={(e) => onChange({ ...content, title: e.target.value })} placeholder="e.g. What to expect" />
    </FieldRow>
    <FieldRow
      label="Body"
      hint="Basic formatting available."
      action={
        <DostPolishTextButton
          value={content.body_html || ''}
          onChange={(v) => onChange({ ...content, body_html: v })}
          isHtml
        />
      }
    >
      <RichTextEditor
        value={content.body_html || ''}
        onChange={(v) => onChange({ ...content, body_html: v })}
        placeholder="Write in your own words…"
      />
    </FieldRow>
  </div>
);

const HeroBlockEditor = ({ content, onChange }) => (
  <div className="space-y-3">
    <FieldRow label="Headline"><Input value={content.title || ''} onChange={(e) => onChange({ ...content, title: e.target.value })} /></FieldRow>
    <FieldRow label="Subheadline"><Input value={content.subtitle || ''} onChange={(e) => onChange({ ...content, subtitle: e.target.value })} /></FieldRow>
    <FieldRow label="Background image URL" hint="For hero image on the Basics tab, use the uploader there instead."><Input value={content.image_url || ''} onChange={(e) => onChange({ ...content, image_url: e.target.value })} placeholder="https://…" /></FieldRow>
  </div>
);

const DateTimeBlockEditor = ({ content, onChange }) => (
  <div className="space-y-3">
    <FieldRow label="Display label"><Input value={content.label || ''} onChange={(e) => onChange({ ...content, label: e.target.value })} placeholder="e.g. Saturday, August 15 · 7 PM CT" /></FieldRow>
    <FieldRow label="Doors open at (optional)"><Input value={content.doors_open || ''} onChange={(e) => onChange({ ...content, doors_open: e.target.value })} /></FieldRow>
    <FieldRow label="Notes"><Textarea rows={2} value={content.notes || ''} onChange={(e) => onChange({ ...content, notes: e.target.value })} /></FieldRow>
  </div>
);

const VenueBlockEditor = ({ content, onChange }) => (
  <div className="space-y-3">
    <FieldRow label="Venue name"><Input value={content.name || ''} onChange={(e) => onChange({ ...content, name: e.target.value })} /></FieldRow>
    <FieldRow label="Address (public description)" hint="Full address is revealed after confirmation - controlled in the Basics tab."><Input value={content.address || ''} onChange={(e) => onChange({ ...content, address: e.target.value })} /></FieldRow>
    <FieldRow
      label="Parking / arrival notes"
      action={
        <DostPolishTextButton
          value={content.arrival_notes || ''}
          onChange={(v) => onChange({ ...content, arrival_notes: v })}
          blockType="venue"
        />
      }
    >
      <Textarea rows={3} value={content.arrival_notes || ''} onChange={(e) => onChange({ ...content, arrival_notes: e.target.value })} />
    </FieldRow>
  </div>
);

const RepeatableList = ({ items, onChange, blank, renderItem, addLabel, emptyHint }) => {
  const list = items || [];
  const add = () => onChange([...list, { ...blank }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const update = (i, patch) => onChange(list.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const move = (i, dir) => {
    const t = i + dir;
    if (t < 0 || t >= list.length) return;
    const arr = [...list];
    [arr[i], arr[t]] = [arr[t], arr[i]];
    onChange(arr);
  };
  return (
    <div className="space-y-2">
      {list.length === 0 && emptyHint && (
        <div className="text-xs text-neutral-500 italic px-1">{emptyHint}</div>
      )}
      {list.map((it, i) => (
        <div key={i} className="border border-neutral-200 rounded-lg p-3 space-y-2 bg-white">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-secondary-700">#{i + 1}</div>
            <div className="flex items-center gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="disabled:opacity-30 p-1 text-neutral-500 hover:text-primary-900" title="Move up"><ChevronUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => move(i, +1)} disabled={i === list.length - 1} className="disabled:opacity-30 p-1 text-neutral-500 hover:text-primary-900" title="Move down"><ChevronDown className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(i)} className="p-1 text-neutral-500 hover:text-accent-700" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
          {renderItem(it, (patch) => update(i, patch), i)}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} data-testid="repeatable-add-btn"><Plus className="h-3.5 w-3.5 mr-1" /> {addLabel || 'Add'}</Button>
    </div>
  );
};

/** Bug 3 stretch: small labeled field for use inside repeatable rows. */
const MiniField = ({ label, hint, max, value, children }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between">
      <Label className="text-[10px] uppercase tracking-widest text-neutral-500 font-medium">{label}</Label>
      {max && (
        <span className={`text-[10px] tabular-nums ${
          (value || '').length > max ? 'text-accent-700' : 'text-neutral-400'
        }`}>
          {(value || '').length}/{max}
        </span>
      )}
    </div>
    {children}
    {hint && <div className="text-[10px] text-neutral-500">{hint}</div>}
  </div>
);

const ScheduleBlockEditor = ({ content, onChange }) => (
  <div className="space-y-3">
    <FieldRow label="Section title"><Input value={content.title || 'Schedule'} onChange={(e) => onChange({ ...content, title: e.target.value })} /></FieldRow>
    <RepeatableList
      items={content.items}
      onChange={(items) => onChange({ ...content, items })}
      blank={{ time: '', title: '', description: '' }}
      addLabel="Add schedule item"
      emptyHint="No schedule items yet. Add the first one below."
      renderItem={(it, up) => (
        <div className="grid gap-3 sm:grid-cols-[130px_1fr]">
          <MiniField label="Time">
            <Input value={it.time || ''} onChange={(e) => up({ time: e.target.value })} placeholder="7:00 PM" />
          </MiniField>
          <MiniField label="Title" max={80} value={it.title || ''}>
            <Input value={it.title || ''} onChange={(e) => up({ title: e.target.value })} placeholder="Doors + light bites" />
          </MiniField>
          <div className="sm:col-span-2">
            <MiniField label="Description (optional)" max={200} value={it.description || ''}>
              <Textarea rows={2} value={it.description || ''} onChange={(e) => up({ description: e.target.value })} placeholder="One line about this segment" />
            </MiniField>
            <div className="flex justify-end mt-1">
              <DostPolishTextButton
                value={it.description || ''}
                onChange={(v) => up({ description: v })}
                blockType="schedule_item"
                size="xs"
                label="Polish"
              />
            </div>
          </div>
        </div>
      )}
    />
  </div>
);

const FaqBlockEditor = ({ content, onChange }) => (
  <div className="space-y-3">
    <FieldRow label="Section title"><Input value={content.title || 'FAQ'} onChange={(e) => onChange({ ...content, title: e.target.value })} /></FieldRow>
    <RepeatableList
      items={content.items}
      onChange={(items) => onChange({ ...content, items })}
      blank={{ question: '', answer: '' }}
      addLabel="Add question"
      emptyHint="No questions yet. Guests love clear FAQs, especially about food, parking, and dress."
      renderItem={(it, up) => (
        <div className="space-y-3">
          <MiniField label="Question" max={120} value={it.question || ''}>
            <Input value={it.question || ''} onChange={(e) => up({ question: e.target.value })} placeholder="Is street parking available?" />
          </MiniField>
          <div>
            <MiniField label="Answer" max={400} value={it.answer || ''}>
              <Textarea rows={3} value={it.answer || ''} onChange={(e) => up({ answer: e.target.value })} placeholder="Yes, meters until 6 PM. After 6 PM it's free." />
            </MiniField>
            <div className="flex justify-end mt-1">
              <DostPolishTextButton
                value={it.answer || ''}
                onChange={(v) => up({ answer: v })}
                blockType="faq_answer"
                size="xs"
                label="Polish answer"
              />
            </div>
          </div>
        </div>
      )}
    />
  </div>
);

const HostPhotoUploader = ({ eventId, hostIndex, currentUrl, onUploaded }) => {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const [pendingFile, setPendingFile] = React.useState(null);

  const _sendUpload = async (file) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('host_index', String(hostIndex));
      const { data } = await api.post(
        `/admin/events/${eventId}/host-photo`, form,
      );
      if (onUploaded && data?.url) onUploaded(data.url);
      toast.success('Host photo uploaded.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Upload failed.';
      toast.error(typeof msg === 'string' ? msg : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const doUpload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error('Please choose an image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB.');
      return;
    }
    if (!eventId) {
      toast.error('Save the event first, then upload host photos.');
      return;
    }
    // Bundle 48.5 - open circle cropper before upload.
    setPendingFile(file);
  };

  const onCropCancel = () => setPendingFile(null);
  const onCropConfirm = async (croppedFile) => {
    setPendingFile(null);
    await _sendUpload(croppedFile);
  };

  return (
    <div
      className={`mt-2 rounded-md border-2 border-dashed p-3 flex items-center gap-3 transition-colors ${
        dragOver ? 'border-primary-600 bg-primary-50' : 'border-neutral-300 bg-neutral-50'
      } ${busy ? 'opacity-70' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) doUpload(f);
      }}
      data-testid={`host-photo-uploader-${hostIndex}`}
    >
      {currentUrl ? (
        <img
          src={currentUrl}
          alt={`Host ${hostIndex + 1}`}
          className="w-14 h-14 rounded-md object-cover border border-neutral-200 shrink-0"
        />
      ) : (
        <div className="w-14 h-14 rounded-md bg-neutral-200 flex items-center justify-center shrink-0">
          <UploadCloud className="h-5 w-5 text-neutral-500" />
        </div>
      )}
      <div className="flex-1 text-xs text-neutral-600">
        <div className="font-medium text-neutral-800">
          {busy ? 'Uploading...' : 'Drop an image here'}
        </div>
        <div className="text-neutral-500">
          jpg, png, webp - up to 5 MB. The URL above updates automatically.
        </div>
      </div>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="text-xs bg-white border border-neutral-300 hover:border-neutral-500 rounded-md px-3 py-1.5 font-medium"
        data-testid={`host-photo-choose-${hostIndex}`}
      >
        Choose file
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) doUpload(f);
        }}
        className="hidden"
      />
      <HostPhotoCropperDialog
        file={pendingFile}
        open={!!pendingFile}
        onCancel={onCropCancel}
        onConfirm={onCropConfirm}
      />
    </div>
  );
};


const HostsBlockEditor = ({ content, onChange, event }) => (
  <div className="space-y-3">
    <FieldRow label="Section title"><Input value={content.title || 'Hosts'} onChange={(e) => onChange({ ...content, title: e.target.value })} /></FieldRow>
    <RepeatableList
      items={content.items}
      onChange={(items) => onChange({ ...content, items })}
      blank={{ name: '', bio: '', image_url: '', link: '' }}
      addLabel="Add host"
      emptyHint="No hosts yet. Add each person who is hosting or co-hosting this event."
      renderItem={(it, up, i) => (
        <div className="grid gap-3 sm:grid-cols-2">
          <MiniField label="Full name" max={80} value={it.name || ''}>
            <Input value={it.name || ''} onChange={(e) => up({ name: e.target.value })} placeholder="e.g. Alex Rivera" />
          </MiniField>
          <MiniField label="Link (optional)" hint="LinkedIn, Instagram, or personal site.">
            <Input value={it.link || ''} onChange={(e) => up({ link: e.target.value })} placeholder="https://linkedin.com/in/..." />
          </MiniField>
          <div className="sm:col-span-2">
            <MiniField label="Short bio" max={280} value={it.bio || ''}>
              <Textarea rows={2} value={it.bio || ''} onChange={(e) => up({ bio: e.target.value })} placeholder="A sentence or two - what they do, why they host." />
            </MiniField>
            <div className="flex justify-end mt-1">
              <DostPolishTextButton
                value={it.bio || ''}
                onChange={(v) => up({ bio: v })}
                blockType="host_bio"
                size="xs"
                label="Polish bio"
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <MiniField label="Photo URL" hint="Paste a URL or drop a file below. Square photo works best.">
              <Input value={it.image_url || ''} onChange={(e) => up({ image_url: e.target.value })} placeholder="https://..." />
            </MiniField>
            <HostPhotoUploader
              eventId={event?.id}
              hostIndex={i}
              currentUrl={it.image_url || ''}
              onUploaded={(url) => up({ image_url: url })}
            />
          </div>
        </div>
      )}
    />
  </div>
);

const SponsorsBlockEditor = ({ content, onChange }) => (
  <div className="space-y-3">
    <FieldRow label="Section title"><Input value={content.title || 'Sponsors'} onChange={(e) => onChange({ ...content, title: e.target.value })} /></FieldRow>
    <RepeatableList
      items={content.items}
      onChange={(items) => onChange({ ...content, items })}
      blank={{ name: '', logo_url: '', url: '' }}
      addLabel="Add sponsor"
      emptyHint="No sponsors yet. Add each brand or partner sponsoring this event."
      renderItem={(it, up) => (
        <div className="grid gap-3 sm:grid-cols-2">
          <MiniField label="Sponsor name" max={60} value={it.name || ''}>
            <Input value={it.name || ''} onChange={(e) => up({ name: e.target.value })} placeholder="e.g. Cavalli Huqqa" />
          </MiniField>
          <MiniField label="Website (optional)">
            <Input value={it.url || ''} onChange={(e) => up({ url: e.target.value })} placeholder="https://…" />
          </MiniField>
          <div className="sm:col-span-2">
            <MiniField label="Logo URL" hint="Transparent PNG works best. Paste a public URL.">
              <Input value={it.logo_url || ''} onChange={(e) => up({ logo_url: e.target.value })} placeholder="https://cdn.example.com/logo.png" />
            </MiniField>
          </div>
        </div>
      )}
    />
  </div>
);

const PoliciesBlockEditor = ({ content, onChange, event }) => {
  // Phase B: full policy editor replaces the standalone Policies tab.
  // Sub-fields live under `content.policies.{key}` so the public renderer
  // and future backend readers can consume the same shape.
  //
  // Bug X3: fall back to the legacy `events.{field}` values when the migrated
  // `content.policies.{key}` is empty - matches the public renderer's fallback
  // chain so the editor never looks empty for events whose data still lives
  // on the legacy doc. Edits always write to `content.policies.*` going
  // forward (never back to the legacy fields). Never sends undefined /
  // "|| ''" as a real value on save unless the admin types something.
  const p = content.policies || {};
  const ev = event || {};
  const val = (k, legacyKey) =>
    (p[k] !== undefined && p[k] !== null && p[k] !== '') ? p[k]
    : (ev[legacyKey] ?? '');
  const setP = (patch) => onChange({ ...content, policies: { ...p, ...patch } });
  return (
    <div className="space-y-4">
      <FieldRow label="Section title">
        <Input value={content.title || 'The details'} onChange={(e) => onChange({ ...content, title: e.target.value })} />
      </FieldRow>

      <FieldRow
        label="Additional narrative (optional)"
        hint="Rendered above the policy tiles on the public page."
        action={
          <DostPolishTextButton
            value={content.body_html || ''}
            onChange={(v) => onChange({ ...content, body_html: v })}
            blockType="policies"
            isHtml
          />
        }
      >
        <RichTextEditor value={content.body_html || ''} onChange={(v) => onChange({ ...content, body_html: v })} />
      </FieldRow>

      <div className="grid gap-4 md:grid-cols-2">
        <PolicyField label="Dress code">
          <Textarea rows={2} value={val('dress_code', 'dress_code')} onChange={(e) => setP({ dress_code: e.target.value })} />
        </PolicyField>
        <PolicyField label="Parking instructions">
          <Textarea rows={2} value={val('parking', 'parking_instructions')} onChange={(e) => setP({ parking: e.target.value })} />
        </PolicyField>
        <PolicyField label="Food details">
          <Textarea rows={2} value={val('food', 'food_details')} onChange={(e) => setP({ food: e.target.value })} />
        </PolicyField>
        <PolicyField label="Dietary notes">
          <Textarea rows={2} value={val('dietary', 'dietary_notes')} onChange={(e) => setP({ dietary: e.target.value })} />
        </PolicyField>
        <PolicyField label="Hookah / smoking">
          <Textarea rows={2} value={val('smoking', 'hookah_smoking_info')} onChange={(e) => setP({ smoking: e.target.value })} />
        </PolicyField>
        <PolicyField label="Accessibility">
          <Textarea rows={2} value={val('accessibility', 'accessibility_info')} onChange={(e) => setP({ accessibility: e.target.value })} />
        </PolicyField>
        <PolicyField label="Minimum age">
          <Input type="number" value={(p.minimum_age ?? ev.minimum_age) ?? ''} onChange={(e) => setP({ minimum_age: e.target.value === '' ? null : Number(e.target.value) })} />
        </PolicyField>
        <PolicyField label="Required identification">
          <Input value={val('id_required', 'required_identification')} onChange={(e) => setP({ id_required: e.target.value })} placeholder="e.g. Government-issued photo ID" />
        </PolicyField>
        <PolicyField label="Refund policy">
          <Textarea rows={3} value={val('refund', 'refund_policy')} onChange={(e) => setP({ refund: e.target.value })} />
        </PolicyField>
        <PolicyField label="Cancellation policy">
          <Textarea rows={3} value={val('cancellation', 'cancellation_policy')} onChange={(e) => setP({ cancellation: e.target.value })} />
        </PolicyField>
        <PolicyField label="Ticket transfer policy">
          <Textarea rows={2} value={val('ticket_transfer', 'ticket_transfer_policy')} onChange={(e) => setP({ ticket_transfer: e.target.value })} />
        </PolicyField>
        <PolicyField label="Event highlights">
          <Textarea rows={2} value={val('photography', 'photography_policy')} onChange={(e) => setP({ photography: e.target.value })} />
        </PolicyField>
        <div className="md:col-span-2">
          <PolicyField label="Event contact email">
            <Input type="email" value={val('contact_email', 'event_contact_email')} onChange={(e) => setP({ contact_email: e.target.value })} placeholder="contact@dostination.com" />
          </PolicyField>
        </div>
      </div>

      {content.migrated_from === 'policy_tab' && (
        <div className="text-[11px] text-neutral-500 italic">
          Migrated from the legacy Policy tab on {content.migrated_at ? content.migrated_at.slice(0, 10) : 'earlier date'}. Original event fields kept as backup and used as fallbacks in this editor when a specific field has not been overridden here yet.
        </div>
      )}
    </div>
  );
};

const PolicyField = ({ label, children }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-widest text-secondary-700">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const FoodBlockEditor = ({ content, onChange }) => (
  <div className="space-y-3">
    <FieldRow label="Section title"><Input value={content.title || 'Food'} onChange={(e) => onChange({ ...content, title: e.target.value })} /></FieldRow>
    <FieldRow
      label="Menu / cuisine notes"
      action={
        <DostPolishTextButton
          value={content.body || content.menu_notes || ''}
          onChange={(v) => onChange({ ...content, body: v })}
          blockType="food"
        />
      }
    >
      <Textarea rows={3} value={content.body || content.menu_notes || ''} onChange={(e) => onChange({ ...content, body: e.target.value })} />
    </FieldRow>
    <FieldRow label="Dietary options offered"><Input value={content.dietary || ''} onChange={(e) => onChange({ ...content, dietary: e.target.value })} placeholder="Vegetarian, halal, etc." /></FieldRow>
  </div>
);

const DressCodeBlockEditor = ({ content, onChange }) => (
  <div className="space-y-3">
    <FieldRow label="Dress code"><Input value={content.dress_code || ''} onChange={(e) => onChange({ ...content, dress_code: e.target.value })} placeholder="Smart casual, cocktail..." /></FieldRow>
    <FieldRow
      label="Notes / description"
      hint="This is what attendees see on the public page."
      action={
        <DostPolishTextButton
          value={content.body || content.notes || ''}
          onChange={(v) => onChange({ ...content, body: v })}
          blockType="dress_code"
        />
      }
    >
      <Textarea rows={3} value={content.body || content.notes || ''} onChange={(e) => onChange({ ...content, body: e.target.value })} />
    </FieldRow>
  </div>
);

const RsvpCtaBlockEditor = ({ content, onChange }) => (
  <div className="space-y-3">
    <FieldRow label="Button label"><Input value={content.button_label || 'RSVP now'} onChange={(e) => onChange({ ...content, button_label: e.target.value })} /></FieldRow>
    <FieldRow
      label="Supporting text"
      action={
        <DostPolishTextButton
          value={content.supporting_text || ''}
          onChange={(v) => onChange({ ...content, supporting_text: v })}
          blockType="rsvp_cta"
        />
      }
    >
      <Textarea rows={2} value={content.supporting_text || ''} onChange={(e) => onChange({ ...content, supporting_text: e.target.value })} placeholder="e.g. Limited to 40 guests." />
    </FieldRow>
  </div>
);

const VideoBlockEditor = ({ content, onChange }) => {
  const url = content.url || '';
  const embed = toEmbedUrl(url);
  return (
    <div className="space-y-3">
      <FieldRow label="Video URL" hint="YouTube or Vimeo.">
        <Input value={url} onChange={(e) => onChange({ ...content, url: e.target.value })} placeholder="https://youtu.be/…" />
      </FieldRow>
      {embed && (
        <div className="aspect-video rounded-lg overflow-hidden border border-neutral-200 bg-black">
          <iframe src={embed} title="Video preview" className="w-full h-full" allow="autoplay; encrypted-media" />
        </div>
      )}
    </div>
  );
};

function toEmbedUrl(u) {
  if (!u) return '';
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = u.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return '';
}

const GalleryBlockEditor = ({ content, onChange }) => {
  const inputRef = useRef(null);
  const items = content.items || [];
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    const next = [...items];
    for (const rawFile of Array.from(files)) {
      try {
        // Bundle 97 - client-side canvas resize + cutover to
        // hardened v2 endpoint. v2 stores bytes-as-is with no
        // Pillow on the request path, so the CF-520 mode that
        // plagued v1 cannot recur.
        const file = await resizeImageIfNeeded(rawFile);
        const fd = new FormData();
        fd.append('file', file);
        fd.append('owner_type', 'event_gallery');
        fd.append('is_public', 'true');
        const { data } = await api.post('/media/upload/v2', fd);
        next.push({ media_id: data.id, url: data.url, alt: '' });
      } catch (e) { /* skip failed */ }
    }
    setBusy(false);
    onChange({ ...content, items: next });
  };

  const update = (i, patch) => onChange({ ...content, items: items.map((x, idx) => idx === i ? { ...x, ...patch } : x) });
  const remove = (i) => onChange({ ...content, items: items.filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const t = i + dir;
    if (t < 0 || t >= items.length) return;
    const arr = [...items];
    [arr[i], arr[t]] = [arr[t], arr[i]];
    onChange({ ...content, items: arr });
  };

  return (
    <div className="space-y-3">
      <FieldRow label="Section title"><Input value={content.title || 'Gallery'} onChange={(e) => onChange({ ...content, title: e.target.value })} /></FieldRow>
      <div className="grid grid-cols-3 gap-2">
        {items.map((it, i) => (
          <div key={i} className="border border-neutral-200 rounded-lg overflow-hidden">
            <div className="aspect-square bg-neutral-100">
              {it.url && <img src={it.url} alt={it.alt || ''} className="w-full h-full object-cover" />}
            </div>
            <div className="p-2 space-y-1">
              <Input value={it.alt || ''} onChange={(e) => update(i, { alt: e.target.value })} placeholder="Alt text" className="text-xs" />
              <div className="flex items-center justify-between">
                <div className="flex">
                  <button onClick={() => move(i, -1)} className="p-1 text-neutral-500 hover:text-primary-900"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => move(i, +1)} className="p-1 text-neutral-500 hover:text-primary-900"><ChevronDown className="h-3.5 w-3.5" /></button>
                </div>
                <button onClick={() => remove(i)} className="p-1 text-neutral-400 hover:text-accent-700"><X className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="aspect-square rounded-lg border-2 border-dashed border-neutral-300 hover:border-secondary-400 hover:bg-secondary-50/30 transition-colors flex flex-col items-center justify-center text-neutral-500 text-xs"
        >
          <Upload className="h-5 w-5 mb-1" />
          {busy ? 'Uploading…' : 'Add photos'}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={(e) => upload(e.target.files)} className="hidden" />
    </div>
  );
};

const DefaultBlockEditor = ({ content, onChange }) => (
  <div className="space-y-3">
    <FieldRow label="Title"><Input value={content.title || ''} onChange={(e) => onChange({ ...content, title: e.target.value })} /></FieldRow>
    <FieldRow
      label="Body"
      action={
        <DostPolishTextButton
          value={content.body || ''}
          onChange={(v) => onChange({ ...content, body: v })}
        />
      }
    >
      <Textarea rows={5} value={content.body || ''} onChange={(e) => onChange({ ...content, body: e.target.value })} />
    </FieldRow>
  </div>
);

/* ------------------------- LIVE PREVIEW ------------------------------- */

const LivePreview = ({ blocks, onSelect }) => {
  const visible = blocks.filter((b) => !b.hidden);
  return (
    <Card className="border shadow-soft" data-testid="live-preview">
      <CardContent className="p-6 space-y-5">
        <div className="text-[11px] uppercase tracking-widest text-secondary-700">Live preview</div>
        {visible.length === 0 ? (
          <div className="text-sm text-neutral-500">Nothing to preview yet. Click a block to edit, or &quot;Add block&quot; to start.</div>
        ) : (
          <div className="space-y-6">
            {visible.map((b) => (
              <button key={b.id} onClick={() => onSelect(b.id)} className="block w-full text-left hover:bg-neutral-50/60 -mx-2 px-2 py-1 rounded-md transition-colors">
                <BlockPreview block={b} />
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const BlockPreview = ({ block }) => {
  const c = block.content || {};
  const meta = BLOCK_META[block.block_type] || { label: block.block_type };
  const title = c.title || meta.label;
  switch (block.block_type) {
    case 'overview':
    case 'what_to_expect':
    case 'custom_rich_text':
    case 'policies':
      return (
        <div>
          <h3 className="font-display text-xl font-bold text-primary-900 mb-1">{title}</h3>
          <div className="prose prose-sm max-w-none text-neutral-700" dangerouslySetInnerHTML={{ __html: c.body_html || '<em class="text-neutral-400">Empty</em>' }} />
        </div>
      );
    case 'date_time':
      return (
        <div>
          <h3 className="font-display text-xl font-bold text-primary-900 mb-1">Date &amp; Time</h3>
          <div className="text-neutral-700">{c.label || <em className="text-neutral-400">Not set</em>}</div>
          {c.doors_open && <div className="text-xs text-neutral-500 mt-1">Doors open {c.doors_open}</div>}
        </div>
      );
    case 'venue':
      return (
        <div>
          <h3 className="font-display text-xl font-bold text-primary-900 mb-1">Venue</h3>
          <div className="text-neutral-700">{c.name || <em className="text-neutral-400">Not set</em>}</div>
          {c.address && <div className="text-sm text-neutral-500 mt-1">{c.address}</div>}
        </div>
      );
    case 'schedule':
      return (
        <div>
          <h3 className="font-display text-xl font-bold text-primary-900 mb-2">{c.title || 'Schedule'}</h3>
          <ul className="space-y-2">
            {(c.items || []).map((it, i) => (
              <li key={i} className="flex gap-3"><span className="font-mono text-xs text-secondary-700 w-20">{it.time || '-'}</span><span className="text-sm text-neutral-700"><span className="font-semibold">{it.title}</span>{it.description && ` - ${it.description}`}</span></li>
            ))}
          </ul>
        </div>
      );
    case 'faq':
      return (
        <div>
          <h3 className="font-display text-xl font-bold text-primary-900 mb-2">{c.title || 'FAQ'}</h3>
          <div className="space-y-2">
            {(c.items || []).map((it, i) => (
              <div key={i}>
                <div className="font-semibold text-primary-900">{it.question}</div>
                <div className="text-sm text-neutral-700">{it.answer}</div>
              </div>
            ))}
          </div>
        </div>
      );
    case 'gallery':
      return (
        <div>
          <h3 className="font-display text-xl font-bold text-primary-900 mb-2">{c.title || 'Gallery'}</h3>
          <div className="grid grid-cols-4 gap-1.5">
            {(c.items || []).slice(0, 8).map((it, i) => (
              <div key={i} className="aspect-square rounded overflow-hidden bg-neutral-100">{it.url && <img src={it.url} alt={it.alt || ''} className="w-full h-full object-cover" />}</div>
            ))}
          </div>
        </div>
      );
    case 'hosts':
      return (
        <div>
          <h3 className="font-display text-xl font-bold text-primary-900 mb-2">{c.title || 'Hosts'}</h3>
          <div className="grid grid-cols-2 gap-3">
            {(c.items || []).map((h, i) => (
              <div key={i} className="flex gap-2">
                {h.image_url && <img src={h.image_url} alt={h.name} className="w-10 h-10 rounded-full object-cover" />}
                <div><div className="font-semibold text-primary-900 text-sm">{h.name}</div><div className="text-xs text-neutral-500">{h.bio}</div></div>
              </div>
            ))}
          </div>
        </div>
      );
    case 'rsvp_cta':
      return (
        <div className="p-4 rounded-xl bg-gradient-to-r from-secondary-100 to-secondary-50 border border-secondary-200 text-center">
          <div className="font-display text-lg font-bold text-primary-900 mb-1">{c.button_label || 'RSVP now'}</div>
          {c.supporting_text && <div className="text-xs text-neutral-600">{c.supporting_text}</div>}
        </div>
      );
    case 'hero':
      return (
        <div className="p-4 rounded-xl bg-gradient-to-br from-primary-900 to-primary-800 text-white">
          <div className="text-[10px] uppercase tracking-widest text-secondary-300 mb-1">Hero</div>
          <div className="font-display text-2xl font-bold">{c.title || 'Untitled'}</div>
          {c.subtitle && <div className="text-sm text-primary-200 mt-1">{c.subtitle}</div>}
        </div>
      );
    case 'video':
      return (
        <div>
          <h3 className="font-display text-xl font-bold text-primary-900 mb-2">Video</h3>
          <div className="text-xs text-neutral-500 font-mono break-all">{c.url || <em className="text-neutral-400">Not set</em>}</div>
        </div>
      );
    default:
      return (
        <div>
          <h3 className="font-display text-xl font-bold text-primary-900">{title}</h3>
          <div className="text-sm text-neutral-500 italic">{meta.label}</div>
        </div>
      );
  }
};

export default VisualBlockEditor;
